/**
 * Deposit rates (Truth in Savings). Bill 2026-10-16: PCB's Rate and Fee Schedule is the Truth-in-Savings
 * account disclosure; its Rate Information and Balance Information sections print from these rows
 * (RATE_SCHED 1.2). Ginger McCullough (Risk) found the samples had no rate information.
 *
 *   /retail/products/deposit-rates                    list (GET v3/retail/admin/deposit-rates)
 *   /retail/products/deposit-rates/tiers/new?product= add a rate tier      (POST)
 *   /retail/products/deposit-rates/tiers/:id          edit / deactivate / reactivate a tier (PUT, DELETE)
 *   /retail/products/deposit-rates/products/:code     a product's Truth-in-Savings terms (PUT, DELETE)
 *
 * Pages, not pop-ups. Every write sends a reason (x-pcb-change-reason) and lands in Change history with
 * who, when, before and after. Every rate cites its source and the date it is valid as of.
 * Who may change it: the product.deposit-rates surface roles (default Admin; Global Admin always).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable } from '@/components/DataTable';
import { BoolField, NumberField, SelectField, TextArea, TextField } from '@/components/FormField';
import { ReasonField, reasonProblem, retailReasonHeader } from '@/components/ChangeReason';

interface Tier {
  tierId: number;
  productCode: string;
  tierOrder: number;
  minBalance: number;
  maxBalance: number | null;
  interestRate: number;
  apy: number;
  notes: string | null;
  validAsOf: string;
  source: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

interface Terms {
  productCode: string;
  tierMethod: 'entire-balance' | 'listed' | string;
  rateIsVariable: boolean;
  compoundingFrequency: string | null;
  creditingFrequency: string | null;
  balanceMethod: string | null;
  accrualBasisDays: number | null;
  tisNotes: string | null;
  source: string;
  isActive: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

interface RateProduct {
  code: string;
  name: string;
  acctType: string;
  category: string;
  minimumDeposit: number | null;
  isActive: boolean;
  interestBearing: boolean;
  terms: Terms | null;
  tiers: Tier[];
  problem: string | null;
}

interface ListResponse {
  success: boolean;
  canWrite: boolean;
  writeRoles: string[];
  products: RateProduct[];
}

interface WriteResponse {
  success: boolean;
  tier?: Tier;
  terms?: Terms;
  error?: string;
  code?: string;
}

const ROUTE = '/v3/retail/admin/deposit-rates';
const BASE = '/retail/products/deposit-rates';
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const pct = (v: number): string => `${v.toFixed(Math.max(2, (String(v).split('.')[1] ?? '').length))}%`;

function errorText(e: unknown): string {
  if (ApiError.isApiError(e)) return e.code ? `${e.message} (${e.code})` : e.message;
  return e instanceof Error ? e.message : String(e);
}

function range(t: Tier): string {
  return t.maxBalance === null ? `${money.format(t.minBalance)} and over` : `${money.format(t.minBalance)} through ${money.format(t.maxBalance)}`;
}

function useRates(): { data: ListResponse | null; error: string | null; reload: () => void } {
  const client = useApiClient();
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    client.http
      .get<ListResponse>(`${ROUTE}?includeInactive=true`)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e));
      });
    return () => {
      cancelled = true;
    };
  }, [client, nonce]);
  return { data, error, reload: () => setNonce((n) => n + 1) };
}

function termsSummary(t: Terms): string {
  const parts = [
    t.tierMethod === 'listed' ? 'tiers printed as listed' : 'tier rate paid on the entire balance',
    t.rateIsVariable ? 'variable rate' : 'fixed rate',
    t.compoundingFrequency ? `compounded ${t.compoundingFrequency}, credited ${t.creditingFrequency}` : 'no compounding sentence',
    t.balanceMethod === 'daily-balance' ? 'daily balance method' : 'no balance-method sentence',
    t.accrualBasisDays ? `${t.accrualBasisDays}-day basis` : 'no accrual-basis sentence',
  ];
  return parts.join('; ');
}

export function DepositRatesListPage(): JSX.Element {
  const { data, error } = useRates();
  const [showAll, setShowAll] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const products = useMemo(
    () => (data?.products ?? []).filter((p) => showAll || p.interestBearing || p.tiers.length > 0 || p.terms !== null),
    [data, showAll],
  );

  return (
    <div>
      <h2>Deposit rates (Truth in Savings)</h2>
      <p className="page-intro">
        The interest rates and annual percentage yields the Rate and Fee Schedule prints in its Rate Information section, and
        the facts behind its Balance Information section. An account without active rates prints fees and limitations only.
        Every rate says where it came from and the date it is valid as of. Changing a rate here changes the schedule for
        accounts opened from now on.
      </p>
      {error && <Banner tone="err">{error}</Banner>}
      {!data && !error && <Spinner />}
      {data && (
        <>
          <div className="toolbar">
            <BoolField label="Show every deposit product" value={showAll} onChange={setShowAll} />
            <BoolField label="Show deactivated tiers" value={showInactive} onChange={setShowInactive} />
            <span className="toolbar__spacer" />
            {!data.canWrite && (
              <span className="form-field__hint">
                Read-only for you. Changes need one of: {[...data.writeRoles, 'SuperAdmin'].join(', ')}.
              </span>
            )}
          </div>
          {products.length === 0 && <div className="empty">No interest-bearing products or rates yet.</div>}
          {products.map((p) => {
            const tiers = p.tiers.filter((t) => showInactive || t.isActive);
            return (
              <Card
                key={p.code}
                title={
                  <>
                    {p.name} <span className="value-clip">({p.code})</span>{' '}
                    {p.interestBearing ? <Pill tone="info">interest-bearing</Pill> : <Pill tone="muted">no interest</Pill>}
                  </>
                }
                actions={
                  data.canWrite ? (
                    <>
                      <Link className="btn btn--ghost btn--sm" to={`${BASE}/products/${encodeURIComponent(p.code)}`}>
                        {p.terms ? 'Edit terms' : 'Add terms'}
                      </Link>{' '}
                      <Link className="btn btn--primary btn--sm" to={`${BASE}/tiers/new?product=${encodeURIComponent(p.code)}`}>
                        Add a tier
                      </Link>
                    </>
                  ) : undefined
                }
              >
                {p.problem && <Banner tone="warn">{p.problem}</Banner>}
                <p className="form-field__hint">
                  Minimum to open: {p.minimumDeposit !== null ? money.format(p.minimumDeposit) : 'not set (Products)'}.{' '}
                  {p.terms ? (
                    <>
                      Terms: {termsSummary(p.terms)}
                      {p.terms.isActive ? '' : ' (deactivated)'}.
                    </>
                  ) : (
                    'No Truth-in-Savings terms.'
                  )}
                </p>
                {p.terms?.tisNotes && <p className="form-field__hint">Printed notes: {p.terms.tisNotes}</p>}
                <DataTable
                  rows={tiers}
                  rowKey={(t) => String(t.tierId)}
                  emptyState={<div className="empty">No rate tiers.</div>}
                  columns={[
                    {
                      header: 'Tier',
                      cell: (t) => (data.canWrite ? <Link to={`${BASE}/tiers/${t.tierId}`}>{t.tierOrder}</Link> : String(t.tierOrder)),
                    },
                    { header: 'Balance', cell: (t) => range(t) },
                    { header: 'Interest rate', cell: (t) => pct(t.interestRate) },
                    { header: 'APY', cell: (t) => pct(t.apy) },
                    { header: 'Valid as of', cell: (t) => t.validAsOf },
                    { header: 'Status', cell: (t) => (t.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">deactivated</Pill>) },
                    { header: 'Source', cell: (t) => <span className="value-clip">{t.source}</span> },
                  ]}
                />
              </Card>
            );
          })}
          <p className="form-field__hint">
            Every change is in <Link to="/retail/history?table=DepositRateTiers">Change history</Link>. The wording these rates
            print in is the Rate and Fee Schedule template (Risk Management panel, Disclosures).
          </p>
        </>
      )}
    </div>
  );
}

// ── Tier editor ─────────────────────────────────────────────────────────────

interface TierForm {
  tierOrder: number | null;
  minBalance: number | null;
  maxBalance: number | null;
  interestRate: number | null;
  apy: number | null;
  validAsOf: string;
  source: string;
  notes: string;
}

const EMPTY_TIER: TierForm = { tierOrder: 1, minBalance: 0, maxBalance: null, interestRate: null, apy: null, validAsOf: '', source: '', notes: '' };

function tierProblem(f: TierForm): string | null {
  if (f.tierOrder === null || f.tierOrder < 1) return 'Give the tier order (1 is the lowest balance tier).';
  if (f.minBalance === null || f.minBalance < 0) return 'Give the lowest balance this tier applies to.';
  if (f.maxBalance !== null && f.maxBalance <= f.minBalance) return 'The highest balance must be above the lowest (leave it empty for "and over").';
  if (f.interestRate === null || f.interestRate < 0 || f.interestRate > 100) return 'Give the interest rate as a percent (2.00 means 2.00%).';
  if (f.apy === null || f.apy < 0 || f.apy > 100) return 'Give the annual percentage yield as a percent.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.validAsOf)) return 'Pick the date the source says the rate is valid as of.';
  if (!f.source.trim()) return 'Say where this rate comes from (the rate sheet, a memo, or who decided and when).';
  return null;
}

export function DepositRateTierEditPage(): JSX.Element {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isNew = id === 'new';
  const tierId = isNew ? null : Number(id);
  const client = useApiClient();
  const navigate = useNavigate();
  const { data, error, reload } = useRates();
  const existing = useMemo(
    () => data?.products.flatMap((p) => p.tiers).find((t) => t.tierId === tierId) ?? null,
    [data, tierId],
  );
  const productCode = existing?.productCode ?? params.get('product') ?? '';
  const product = data?.products.find((p) => p.code === productCode) ?? null;

  const [form, setForm] = useState<TierForm>(EMPTY_TIER);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setForm({
        tierOrder: existing.tierOrder,
        minBalance: existing.minBalance,
        maxBalance: existing.maxBalance,
        interestRate: existing.interestRate,
        apy: existing.apy,
        validAsOf: existing.validAsOf,
        source: existing.source,
        notes: existing.notes ?? '',
      });
    } else if (isNew && product) {
      const next = Math.max(0, ...product.tiers.filter((t) => t.isActive).map((t) => t.tierOrder)) + 1;
      setForm((f) => ({ ...f, tierOrder: next }));
    }
  }, [existing, isNew, product]);

  const write = useCallback(
    async (kind: 'save' | 'deactivate' | 'reactivate') => {
      setTried(true);
      setSaveError(null);
      setDone(null);
      if (reasonProblem(reason)) return;
      if (kind !== 'deactivate' && tierProblem(form)) return;
      setSaving(true);
      try {
        const headers = retailReasonHeader(reason);
        const body = { ...form, productCode, notes: form.notes.trim() || null };
        let r: WriteResponse;
        if (kind === 'deactivate') r = await client.http.del<WriteResponse>(`${ROUTE}/${tierId}`, { headers });
        else if (isNew) r = await client.http.post<WriteResponse>(ROUTE, { headers, body: { ...body, isActive: true } });
        else r = await client.http.put<WriteResponse>(`${ROUTE}/${tierId}`, { headers, body: { ...body, isActive: kind === 'reactivate' ? true : existing?.isActive } });
        if (!r.success) throw new Error(r.error ?? 'The change was not saved.');
        if (isNew && r.tier) {
          navigate(`${BASE}/tiers/${r.tier.tierId}`, { replace: true });
          return;
        }
        setReason('');
        setTried(false);
        setDone(kind === 'deactivate' ? 'Deactivated.' : kind === 'reactivate' ? 'Reactivated.' : 'Saved.');
        reload();
      } catch (e) {
        setSaveError(errorText(e));
      } finally {
        setSaving(false);
      }
    },
    [client, existing, form, isNew, navigate, productCode, reason, reload, tierId],
  );

  const back = <Link to={BASE}>Back to deposit rates</Link>;
  if (error) return <div>{back}<Banner tone="err">{error}</Banner></div>;
  if (!data) return <Spinner />;
  if (!isNew && !existing) return <div>{back}<div className="empty">Rate tier #{id} not found.</div></div>;
  if (!product) return <div>{back}<div className="empty">Pick a product from the deposit rates list.</div></div>;
  if (!data.canWrite) return <div><p>{back}</p><Banner tone="info">You can read the rates but not change them.</Banner></div>;

  const problem = tried ? tierProblem(form) : null;
  const setNum = (k: 'tierOrder' | 'minBalance' | 'maxBalance' | 'interestRate' | 'apy') => (v: number | null) => setForm((f) => ({ ...f, [k]: v }));
  const setStr = (k: 'validAsOf' | 'source' | 'notes') => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <p>{back}</p>
      <h2>{isNew ? `Add a rate tier — ${product.name}` : `${product.name} — tier ${existing!.tierOrder}`}</h2>
      {existing && (
        <p className="form-field__hint">
          {existing.isActive ? 'Active.' : 'Deactivated.'} Added {new Date(existing.createdAt).toLocaleString()}
          {existing.createdBy ? ` by ${existing.createdBy}` : ''}; last changed {new Date(existing.updatedAt).toLocaleString()}
          {existing.updatedBy ? ` by ${existing.updatedBy}` : ''}.
        </p>
      )}
      {done && <Banner tone="info">{done}</Banner>}
      <Card title="The tier">
        <NumberField label="Tier order" required value={form.tierOrder} onChange={setNum('tierOrder')} min={1} step={1} hint="1 is the lowest balance tier. Tiers must run from $0.00 upward with no gaps; only the last has no highest balance." />
        <NumberField label="Lowest balance ($)" required value={form.minBalance} onChange={setNum('minBalance')} min={0} step={0.01} />
        <NumberField label="Highest balance ($)" value={form.maxBalance} onChange={setNum('maxBalance')} min={0} step={0.01} hint='Leave empty for the top tier ("and over").' />
        <NumberField label="Interest rate (%)" required value={form.interestRate} onChange={setNum('interestRate')} min={0} max={100} step={0.0001} hint="2.00 means 2.00%." />
        <NumberField label="Annual percentage yield (%)" required value={form.apy} onChange={setNum('apy')} min={0} max={100} step={0.0001} />
        <label className="form-field">
          <span className="form-field__label">
            Valid as of<span className="form-field__required" aria-hidden> *</span>
          </span>
          <input type="date" className="form-input" value={form.validAsOf} onChange={(e) => setStr('validAsOf')(e.target.value)} />
        </label>
        <TextField label="Source" required value={form.source} onChange={setStr('source')} maxLength={512} hint="Where this rate comes from, e.g. https://www.mypcb.com/rates and who supplied it." />
        <TextArea label="Internal notes (not printed)" value={form.notes} onChange={setStr('notes')} />
        {problem && <Banner tone="err">{problem}</Banner>}
        <ReasonField value={reason} onChange={setReason} showError={tried} />
        {saveError && <Banner tone="err">{saveError}</Banner>}
        <div className="card-actions-row">
          <Button onClick={() => void write('save')} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Add tier' : 'Save changes'}
          </Button>
          {existing && existing.isActive && (
            <Button variant="danger" onClick={() => void write('deactivate')} disabled={saving}>
              Deactivate
            </Button>
          )}
          {existing && !existing.isActive && (
            <Button variant="ghost" onClick={() => void write('reactivate')} disabled={saving}>
              Reactivate
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

// ── Terms editor ────────────────────────────────────────────────────────────

interface TermsForm {
  tierMethod: string;
  rateIsVariable: boolean;
  compoundingFrequency: string;
  creditingFrequency: string;
  balanceMethod: string;
  accrualBasisDays: string;
  tisNotes: string;
  source: string;
}

const EMPTY_TERMS: TermsForm = {
  tierMethod: 'entire-balance',
  rateIsVariable: true,
  compoundingFrequency: '',
  creditingFrequency: '',
  balanceMethod: '',
  accrualBasisDays: '',
  tisNotes: '',
  source: '',
};

const FREQUENCIES = [
  { value: '', label: '(not printed)' },
  { value: 'daily', label: 'daily' },
  { value: 'monthly', label: 'monthly' },
  { value: 'quarterly', label: 'quarterly' },
  { value: 'annually', label: 'annually' },
];

function termsProblem(f: TermsForm): string | null {
  if (!f.compoundingFrequency !== !f.creditingFrequency) return 'Set compounding and crediting together; the schedule prints them in one sentence.';
  if (!f.source.trim()) return 'Say where these facts come from (a sample schedule and page, a memo, or who decided and when).';
  return null;
}

export function DepositRateTermsEditPage(): JSX.Element {
  const { code } = useParams();
  const client = useApiClient();
  const { data, error, reload } = useRates();
  const product = data?.products.find((p) => p.code === code) ?? null;
  const existing = product?.terms ?? null;

  const [form, setForm] = useState<TermsForm>(EMPTY_TERMS);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setForm({
        tierMethod: existing.tierMethod,
        rateIsVariable: existing.rateIsVariable,
        compoundingFrequency: existing.compoundingFrequency ?? '',
        creditingFrequency: existing.creditingFrequency ?? '',
        balanceMethod: existing.balanceMethod ?? '',
        accrualBasisDays: existing.accrualBasisDays ? String(existing.accrualBasisDays) : '',
        tisNotes: existing.tisNotes ?? '',
        source: existing.source,
      });
    }
  }, [existing]);

  const write = useCallback(
    async (kind: 'save' | 'deactivate' | 'reactivate') => {
      setTried(true);
      setSaveError(null);
      setDone(null);
      if (reasonProblem(reason)) return;
      if (kind !== 'deactivate' && termsProblem(form)) return;
      setSaving(true);
      try {
        const headers = retailReasonHeader(reason);
        const url = `${ROUTE}/products/${encodeURIComponent(code ?? '')}`;
        const r =
          kind === 'deactivate'
            ? await client.http.del<WriteResponse>(url, { headers })
            : await client.http.put<WriteResponse>(url, {
                headers,
                body: {
                  tierMethod: form.tierMethod,
                  rateIsVariable: form.rateIsVariable,
                  compoundingFrequency: form.compoundingFrequency || null,
                  creditingFrequency: form.creditingFrequency || null,
                  balanceMethod: form.balanceMethod || null,
                  accrualBasisDays: form.accrualBasisDays ? Number(form.accrualBasisDays) : null,
                  tisNotes: form.tisNotes.trim() || null,
                  source: form.source,
                  isActive: kind === 'reactivate' ? true : (existing?.isActive ?? true),
                },
              });
        if (!r.success) throw new Error(r.error ?? 'The change was not saved.');
        setReason('');
        setTried(false);
        setDone(kind === 'deactivate' ? 'Deactivated.' : kind === 'reactivate' ? 'Reactivated.' : 'Saved.');
        reload();
      } catch (e) {
        setSaveError(errorText(e));
      } finally {
        setSaving(false);
      }
    },
    [client, code, existing, form, reason, reload],
  );

  const back = <Link to={BASE}>Back to deposit rates</Link>;
  if (error) return <div>{back}<Banner tone="err">{error}</Banner></div>;
  if (!data) return <Spinner />;
  if (!product) return <div>{back}<div className="empty">{code} is not a deposit product.</div></div>;
  if (!data.canWrite) return <div><p>{back}</p><Banner tone="info">You can read the terms but not change them.</Banner></div>;

  const problem = tried ? termsProblem(form) : null;
  const set = (k: keyof TermsForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div>
      <p>{back}</p>
      <h2>{product.name} — Truth-in-Savings terms</h2>
      <p className="page-intro">
        Each fact picks one sentence of the Rate and Fee Schedule (the Deposit Pro wording). A fact left empty prints no
        sentence. Change the wording itself in the template, not here.
      </p>
      {existing && (
        <p className="form-field__hint">
          {existing.isActive ? 'Active.' : 'Deactivated.'} Last changed {new Date(existing.updatedAt).toLocaleString()}
          {existing.updatedBy ? ` by ${existing.updatedBy}` : ''}.
        </p>
      )}
      {done && <Banner tone="info">{done}</Banner>}
      <Card title="Terms">
        <SelectField
          label="How tiers print"
          value={form.tierMethod}
          onChange={set('tierMethod')}
          options={[
            { value: 'entire-balance', label: '"If the daily balance is ... the interest rate paid on the entire balance ..."' },
            { value: 'listed', label: 'As a table, as the rate sheet lists them (no tiering sentence)' },
          ]}
          hint="Only matters with two or more tiers."
        />
        <BoolField
          label='Variable rate ("The interest rate and annual percentage yield may change ...")'
          value={form.rateIsVariable}
          onChange={(v) => setForm((f) => ({ ...f, rateIsVariable: v }))}
        />
        <SelectField label="Interest is compounded" value={form.compoundingFrequency} onChange={set('compoundingFrequency')} options={FREQUENCIES} />
        <SelectField label="Interest is credited" value={form.creditingFrequency} onChange={set('creditingFrequency')} options={FREQUENCIES} />
        <SelectField
          label="Balance computation method"
          value={form.balanceMethod}
          onChange={set('balanceMethod')}
          options={[
            { value: '', label: '(no Balance Information section)' },
            { value: 'daily-balance', label: 'Daily balance method' },
          ]}
        />
        <SelectField
          label="Interest accrual basis"
          value={form.accrualBasisDays}
          onChange={set('accrualBasisDays')}
          options={[
            { value: '', label: '(not printed)' },
            { value: '365', label: '365 days' },
            { value: '360', label: '360 days' },
          ]}
        />
        <TextArea label="Printed notes" value={form.tisNotes} onChange={set('tisNotes')} hint="Printed as its own paragraph in Rate Information, e.g. qualification requirements." />
        <TextField label="Source" required value={form.source} onChange={set('source')} maxLength={1024} hint="Where these facts come from: a sample schedule and page, the rate sheet, a memo." />
        {problem && <Banner tone="err">{problem}</Banner>}
        <ReasonField value={reason} onChange={setReason} showError={tried} />
        {saveError && <Banner tone="err">{saveError}</Banner>}
        <div className="card-actions-row">
          <Button onClick={() => void write('save')} disabled={saving}>
            {saving ? 'Saving…' : existing ? 'Save changes' : 'Add terms'}
          </Button>
          {existing && existing.isActive && (
            <Button variant="danger" onClick={() => void write('deactivate')} disabled={saving}>
              Deactivate
            </Button>
          )}
          {existing && !existing.isActive && (
            <Button variant="ghost" onClick={() => void write('reactivate')} disabled={saving}>
              Reactivate
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
